import type { ReactElement } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { History, PanelLeftClose, PanelLeftOpen, Search, SquarePen, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui';
import { PlanQuotaBadge } from '@/components/billing';
import { NEW_CONVERSATION_PATH } from '@/components/conversations';
import { useAuth } from '@/lib/auth';
import type { Role } from '@/lib/api/types';
import { APP_BASE, CONVERSATIONS_PATH, NAV_ITEMS, navItemVisible, type NavItem } from '../navigation';
import { SideNavHistory } from './SideNavHistory';
import { UserMenu } from './UserMenu';
import { CollapsibleWordmark } from '@/components/brand';
import './SideNav.css';

/** Configurações mora no rodapé, junto da conta: é sobre a pessoa, não um destino de trabalho. */
const FOOTER_PATH = '/app/configuracoes';

const ICON_PROPS = { size: 18, strokeWidth: 1.75 } as const;

/** O atalho de "Procurar conversas", anunciado no tooltip e no `aria-keyshortcuts`. */
const SEARCH_SHORTCUT = 'Control+K';
const SEARCH_TOOLTIP = 'Procurar conversas (Ctrl K)';

/** O que uma linha da barra precisa saber de um destino. */
type SideNavLinkItem = Pick<NavItem, 'path' | 'label' | 'icon'>;

/** No rail, as conversas viram um ícone que leva à tela de todas elas. */
const CONVERSATIONS_LINK: SideNavLinkItem = { path: CONVERSATIONS_PATH, label: 'Conversas', icon: History };

export interface SideNavProps {
  /** Current user's role, used to gate items. */
  role: Role | undefined;
  /**
   * As capabilities do plano (`/v1/me`), para o gating comercial dos itens.
   * `undefined` enquanto o /me carrega — e aí nada se esconde, senão a barra
   * piscaria a cada navegação com o cache de 5 minutos vencido.
   */
  planCapabilities?: string[];
  /** Icon-only rail when collapsed. */
  collapsed: boolean;
  /**
   * A gaveta das telas estreitas (nunca recolhida), no padrão do app do Gemini:
   * alvos de toque maiores, o X que a fecha, "Novo chat" aceso na conversa nova,
   * o link para todas as conversas sempre à vista e o rodapé preso ao pé.
   */
  drawer?: boolean;
  /**
   * O controle da barra, no topo dela: recolhe/expande o rail no desktop e, na
   * gaveta (telas estreitas, nunca recolhida), fecha a gaveta.
   */
  onToggleCollapsed: () => void;
  /** Off-canvas mode on narrow widths; closes on navigation. */
  onNavigate?: () => void;
  /** Abre o diálogo "Procurar conversas" (mora no AppShell, com o atalho). */
  onOpenSearch: () => void;
  /** "Perguntar à Shiba" no menu da conta; ausente onde o widget não é montado. */
  onAskShiba?: () => void;
}

/**
 * Primary navigation on the shell's flat surface (`--shell-bg`), sem filete: o
 * que se destaca dela é o painel do conteúdo, ao lado. A linha do topo
 * segue o Gemini: o ∫hibΔ à esquerda (nome acessível "Shiba") e, à direita,
 * dentro da barra, "Procurar conversas" (Ctrl K) e "Fechar barra lateral".
 * Depois "Novo chat" e os itens de navegação como linhas de texto (a rota ativa
 * numa pílula suave), todos no mesmo ritmo, e as "Conversas" de quem entrou. O
 * rodapé tem a cota do plano gratuito e o chip da conta (o primeiro nome) com a
 * engrenagem de Configurações à direita. Só aparecem os itens que o papel e o
 * plano permitem.
 *
 * Recolhida, vira o rail do Gemini: o topo é um botão só, "Abrir barra lateral",
 * que em repouso mostra a marca reduzida ("iΔ") e no hover/foco troca para o
 * ícone de abrir. Abaixo, as ações como botões de ícone sem rótulo (nome no
 * `aria-label`, rótulo visual no tooltip) — "Procurar conversas" volta para a
 * coluna, e as conversas, que não cabem no rail, viram o ícone que leva à tela
 * delas — e o rodapé fixo, com Configurações e o avatar.
 *
 * Os ícones ficam na MESMA posição horizontal nos dois estados: cada linha é uma
 * célula de ícone de 40px no mesmo recuo, e expandida só ganha o rótulo à direita.
 *
 * Como gaveta do celular (`drawer`), segue a gaveta do app do Gemini: os dois
 * botões do topo viram discos redondos e o controle da barra é o X que a fecha;
 * as linhas crescem para o toque, "Novo chat" acende na conversa nova, "Ver
 * todas" fica sempre à vista e o rodapé, com o avatar maior e o plano sob o
 * primeiro nome, fica preso ao pé.
 */
export function SideNav({
  role,
  planCapabilities,
  collapsed,
  drawer = false,
  onToggleCollapsed,
  onNavigate,
  onOpenSearch,
  onAskShiba,
}: SideNavProps): JSX.Element {
  const { status } = useAuth();
  const { pathname, search } = useLocation();
  const items = NAV_ITEMS.filter((item) => navItemVisible(item, role, planCapabilities));
  const main = items.filter((item) => item.path !== FOOTER_PATH);
  const settings = items.find((item) => item.path === FOOTER_PATH);
  const signedIn = status === 'authenticated';
  const toggleLabel = collapsed ? 'Abrir barra lateral' : 'Fechar barra lateral';
  // Só a pílula, sem aria-current: quem diz a página atual é "Conhecimento", e
  // dois links não disputam esse estado.
  const newChatActive = drawer && `${pathname}${search}` === NEW_CONVERSATION_PATH;

  return (
    <nav
      className={cn('side-nav', collapsed && 'side-nav--collapsed', drawer && 'side-nav--drawer')}
      aria-label="Navegação principal"
    >
      {/* A marca e o controle da barra são os MESMOS elementos nos dois estados
          (posições fixas nesta linha): a marca anima entre ∫hibΔ e iΔ, e o foco
          continua no controle depois de recolher ou expandir. */}
      <div className="side-nav__brand">
        <span className="side-nav__name">
          <CollapsibleWordmark height={36} collapsed={collapsed} label="Shiba" className="side-nav__logo" />
        </span>
        {!collapsed && (
          <Tooltip content={SEARCH_TOOLTIP} side="bottom">
            <button
              type="button"
              className="side-nav__tool"
              aria-label="Procurar conversas"
              aria-keyshortcuts={SEARCH_SHORTCUT}
              onClick={onOpenSearch}
            >
              <Search {...ICON_PROPS} aria-hidden="true" />
            </button>
          </Tooltip>
        )}
        <Tooltip content={toggleLabel} side={collapsed ? 'right' : 'bottom'}>
          <button
            type="button"
            className="side-nav__tool side-nav__toggle"
            aria-label={toggleLabel}
            aria-expanded={!collapsed}
            onClick={onToggleCollapsed}
          >
            <ToggleIcon collapsed={collapsed} drawer={drawer} />
          </button>
        </Tooltip>
      </div>

      <div className="side-nav__actions">
        <RailTooltip collapsed={collapsed} label="Novo chat">
          <Link
            to={NEW_CONVERSATION_PATH}
            className={cn('side-nav__link', 'side-nav__action', newChatActive && 'side-nav__link--active')}
            aria-label={collapsed ? 'Novo chat' : undefined}
            onClick={onNavigate}
          >
            <span className="side-nav__icon" aria-hidden="true">
              <SquarePen {...ICON_PROPS} />
            </span>
            {!collapsed && <span className="side-nav__label">Novo chat</span>}
          </Link>
        </RailTooltip>
        {collapsed && (
          <>
            <RailTooltip collapsed label={SEARCH_TOOLTIP}>
              <button
                type="button"
                className="side-nav__link side-nav__action"
                aria-label="Procurar conversas"
                aria-keyshortcuts={SEARCH_SHORTCUT}
                onClick={onOpenSearch}
              >
                <span className="side-nav__icon" aria-hidden="true">
                  <Search {...ICON_PROPS} />
                </span>
              </button>
            </RailTooltip>
            <SideNavLink item={CONVERSATIONS_LINK} collapsed onNavigate={onNavigate} />
          </>
        )}
      </div>

      <ul className="side-nav__list" role="list">
        {main.map((item) => (
          <li key={item.path}>
            <SideNavLink item={item} collapsed={collapsed} onNavigate={onNavigate} />
          </li>
        ))}
      </ul>

      {signedIn && !collapsed && <SideNavHistory onNavigate={onNavigate} drawer={drawer} />}

      {/* O chip da conta fica sempre na mesma posição desta árvore: recolher e
          expandir não o remontam (nem o menu nem o diálogo de senha dele). A
          cota, como as conversas, é de quem entrou. */}
      <div className="side-nav__footer">
        {signedIn && !collapsed && <PlanQuotaBadge />}
        {collapsed && settings && <SideNavLink item={settings} collapsed onNavigate={onNavigate} />}
        <div className="side-nav__account">
          <UserMenu
            collapsed={collapsed}
            avatarSize={drawer ? 'lg' : 'sm'}
            showPlan={drawer}
            onAskShiba={onAskShiba}
          />
          {!collapsed && settings && <SettingsShortcut item={settings} onNavigate={onNavigate} />}
        </div>
      </div>
    </nav>
  );
}

/** O controle da barra: abre o rail, fecha a gaveta (o X do Gemini) ou recolhe a barra. */
function ToggleIcon({ collapsed, drawer }: { collapsed: boolean; drawer: boolean }): JSX.Element {
  if (collapsed) return <PanelLeftOpen {...ICON_PROPS} aria-hidden="true" className="side-nav__toggle-icon" />;
  if (drawer) return <X {...ICON_PROPS} aria-hidden="true" />;
  return <PanelLeftClose {...ICON_PROPS} aria-hidden="true" />;
}

/** No rail o rótulo visual some e vira tooltip; expandida, o texto já está lá. */
function RailTooltip({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: ReactElement;
}): JSX.Element {
  return collapsed ? (
    <Tooltip content={label} side="right">
      {children}
    </Tooltip>
  ) : (
    children
  );
}

interface SideNavLinkProps {
  item: SideNavLinkItem;
  collapsed: boolean;
  onNavigate?: () => void;
}

function SideNavLink({ item, collapsed, onNavigate }: SideNavLinkProps): JSX.Element {
  return (
    <RailTooltip collapsed={collapsed} label={item.label}>
      <NavLink
        to={item.path}
        end={item.path === APP_BASE}
        aria-label={collapsed ? item.label : undefined}
        className={({ isActive }) => cn('side-nav__link', isActive && 'side-nav__link--active')}
        onClick={onNavigate}
      >
        <span className="side-nav__icon" aria-hidden="true">
          <item.icon {...ICON_PROPS} />
        </span>
        {!collapsed && <span className="side-nav__label">{item.label}</span>}
      </NavLink>
    </RailTooltip>
  );
}

/**
 * Expandida, Configurações é a engrenagem à direita do chip da conta. Link, e não
 * botão: é um destino, que abre em outra aba e diz quando é a página atual — sem
 * a linha de texto no rodapé, é ela que acende em Configurações.
 */
function SettingsShortcut({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }): JSX.Element {
  return (
    <Tooltip content={item.label} side="top">
      <NavLink
        to={item.path}
        aria-label={item.label}
        className={({ isActive }) => cn('side-nav__tool', 'side-nav__settings', isActive && 'side-nav__tool--active')}
        onClick={onNavigate}
      >
        <item.icon {...ICON_PROPS} aria-hidden="true" />
      </NavLink>
    </Tooltip>
  );
}
